import {registerGlobalInitFunc} from '../modules/observer.ts';
import {GET} from '../modules/fetch.ts';
import {createElementFromHTML} from '../utils/dom.ts';

const MARGIN_LEFT = 60;
const MARGIN_TOP = 80;
const COLUMN_WIDTH = 180;
const ROW_HEIGHT = 70;
// Tall enough to clear the two-line, bordered entity label (name line + team
// line + the rect's vertical padding) before the lifeline begins. Kept >= 48
// per the interface spec so the rounded box never collides with the lifeline.
const LIFELINE_HEIGHT = 56;
const MAX_CONTAINER_HEIGHT = 480;
const BASE_ANIMATION_DURATION = 800;
const VIRTUALIZATION_THRESHOLD = 100;
const BUFFER_ROWS = 2;

// Live-refresh poll interval (ms) for the Assignment History panel. The panel is
// server-rendered once at page load; Overwatch records new assignment/pipeline
// events out-of-band, so without this the panel is stale until a full reload. The
// value can be overridden per-render via data-refresh-interval on the container.
const DEFAULT_REFRESH_INTERVAL = 10000;

// Entity-label box geometry. The rect is sized from getBBox() after attach, then
// inflated by this padding; rx/ry are applied as SVG attributes (not CSS) so the
// corners round in every renderer regardless of CSS rx/ry support.
const ENTITY_BOX_PAD_X = 8;
const ENTITY_BOX_PAD_Y = 4;
const ENTITY_BOX_RADIUS = 4;
// Vertical offset of the team (second) line relative to the name line baseline.
const TEAM_LINE_DY = '1.2em';

type PipelineTrace = {
  issue_id: number;
  repo_id: number;
  event_count: number;
  is_live: boolean;
  truncated: boolean;
  events: PipelineEvent[];
  creator?: string;
};

type PipelineEvent = {
  id: number;
  seq: number;
  timestamp: string;
  from_entity: string;
  to_entity: string;
  from_team?: string;
  to_team?: string;
  doer_avatar: string;
  affected_user_avatar: string;
  label: string;
  direction: 'request' | 'response';
  duration_ms: number | null;
  parent_event_id: number | null;
  metadata: Record<string, unknown> | null;
};

export function computeEntityOrder(events: PipelineEvent[], creator?: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  const actualCreator = creator || events[0]?.from_entity || '';
  if (actualCreator) {
    ordered.push(actualCreator);
    seen.add(actualCreator);
  }

  for (const event of events) {
    for (const entity of [event.from_entity, event.to_entity]) {
      if (entity && !seen.has(entity)) {
        seen.add(entity);
        ordered.push(entity);
      }
    }
  }

  return ordered;
}

function buildEntityList(trace: PipelineTrace): string[] {
  return computeEntityOrder(trace.events, trace.creator);
}

// computeEntityTeams maps each entity name to the team that should appear on the
// second label line. The trace carries the team per event endpoint, so the map
// is built by associating each entity with the team recorded alongside it.
export function computeEntityTeams(events: PipelineEvent[]): Map<string, string> {
  const teams = new Map<string, string>();
  for (const event of events) {
    if (event.from_entity && event.from_team && !teams.has(event.from_entity)) {
      teams.set(event.from_entity, event.from_team);
    }
    if (event.to_entity && event.to_team && !teams.has(event.to_entity)) {
      teams.set(event.to_entity, event.to_team);
    }
  }
  return teams;
}

export function getVisibleSlice(totalEvents: number, scrollTop: number, viewportRows: number, bufferRows = BUFFER_ROWS): {start: number; end: number} {
  const firstVisibleRow = Math.floor(scrollTop / ROW_HEIGHT);
  const end = Math.min(totalEvents, firstVisibleRow + viewportRows + bufferRows);
  const start = Math.min(Math.max(0, firstVisibleRow - bufferRows), end);
  return {start, end};
}

function createSVGPath(d: string, className?: string): SVGPathElement {
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  if (className) path.setAttribute('class', className);
  return path;
}

function createSVGText(x: number, y: number, text: string, className?: string): SVGTextElement {
  const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  textEl.setAttribute('x', String(x));
  textEl.setAttribute('y', String(y));
  textEl.setAttribute('text-anchor', 'middle');
  if (className) textEl.setAttribute('class', className);
  textEl.textContent = text;
  return textEl;
}

// createSVGTSpan builds a <tspan>; setting the x keeps each line centred under
// the same column, and dy stacks the second line below the first. Text is set via
// textContent (never innerHTML) so usernames/team names cannot inject markup.
function createSVGTSpan(x: number, text: string, className: string, dy?: string): SVGTSpanElement {
  const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
  tspan.setAttribute('x', String(x));
  if (dy) tspan.setAttribute('dy', dy);
  tspan.setAttribute('class', className);
  tspan.textContent = text;
  return tspan;
}

function createSVGRect(className: string): SVGRectElement {
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('class', className);
  return rect;
}

function createSVGLine(x1: number, y1: number, x2: number, y2: number, className?: string): SVGLineElement {
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line.setAttribute('x1', String(x1));
  line.setAttribute('y1', String(y1));
  line.setAttribute('x2', String(x2));
  line.setAttribute('y2', String(y2));
  if (className) line.setAttribute('class', className);
  return line;
}

function createSVGTitle(text: string): SVGTitleElement {
  const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
  title.textContent = text;
  return title;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// setupPipelineLiveRefresh keeps the Assignment History panel current without a
// full page reload. It reuses Gitea's own partial-refresh seam (the pattern that
// polls a bare fragment endpoint and swaps the element via replaceWith, as
// initRepoPullMergeBox does for the merge box): it re-fetches the server-rendered
// panel fragment on a visibility-gated interval and replaces this element with the
// fresh one. Gitea's global-init MutationObserver then re-runs initGestaltPipeline
// on the replacement (a fresh element has no _giteaGlobalInited flag), which
// redraws from the new trace AND re-arms a fresh poller. The old poller stops once
// it has handed off, so exactly one poller runs at a time.
//
// Because the whole element is replaced, the redraw is inherently idempotent (no
// duplicate arrows) and the reduced-motion handling is re-evaluated on each pass.
// It is a no-op when data-refresh-url is absent (e.g. an older render), so the
// panel degrades to its prior load-time-only behaviour rather than erroring.
function setupPipelineLiveRefresh(container: HTMLElement): void {
  const refreshURL = container.getAttribute('data-refresh-url');
  if (!refreshURL) return; // live refresh not configured; keep static behaviour

  const intervalAttr = container.getAttribute('data-refresh-interval');
  const parsed = intervalAttr ? parseInt(intervalAttr) : DEFAULT_REFRESH_INTERVAL;
  const interval = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_REFRESH_INTERVAL;

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let refresh: () => Promise<void>;

  const stop = (): void => {
    if (timerId === null) return;
    clearTimeout(timerId);
    timerId = null;
  };
  const start = (): void => {
    if (timerId !== null) return;
    timerId = setTimeout(refresh, interval);
  };
  // Pause polling while the tab is hidden (mirrors initRepoPullMergeBox) so a
  // backgrounded issue page does not keep hitting the endpoint.
  const onVisibilityChange = (): void => {
    if (document.hidden) stop(); else start();
  };

  refresh = async (): Promise<void> => {
    timerId = null;
    // If a prior refresh already replaced this element, this closure is stale:
    // detach its listener and let the replacement's poller take over.
    if (!container.isConnected) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      return;
    }
    try {
      const resp = await GET(refreshURL);
      if (!resp.ok) {
        start(); // transient failure: keep the current panel, try again later
        return;
      }
      const html = await resp.text();
      const newEl = createElementFromHTML<HTMLElement>(html);
      if (!newEl) {
        // Empty fragment (panel no longer rendered, e.g. trace became nil): leave
        // the existing panel in place and keep polling in case it returns.
        start();
        return;
      }
      // Hand off: stop this poller and swap in the fresh panel. The global-init
      // observer re-initialises the replacement, which arms its own poller.
      document.removeEventListener('visibilitychange', onVisibilityChange);
      container.replaceWith(newEl);
    } catch {
      start(); // network error: retry on the next tick rather than giving up
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  start();
}

export function initGestaltPipeline(container: HTMLElement): void {
  const scriptEl = container.querySelector<HTMLScriptElement>('#pipeline-trace-data');
  if (!scriptEl) {
    console.error('gestalt-pipeline: #pipeline-trace-data not found');
    return;
  }

  let trace: PipelineTrace;
  try {
    trace = JSON.parse(scriptEl.textContent || '{}') as PipelineTrace;
  } catch (e) {
    console.error('gestalt-pipeline: failed to parse trace data', e);
    return;
  }

  if (!trace.events || !Array.isArray(trace.events)) {
    console.error('gestalt-pipeline: invalid trace data structure');
    return;
  }

  // Arm the live-refresh poller now, before the empty-state early return, so a
  // panel that currently has zero events still picks up the first assignment
  // event live rather than only on the next full page reload.
  setupPipelineLiveRefresh(container);

  if (trace.events.length === 0) {
    // Empty assignment history; template renders a static empty state and emits
    // no controls/canvas, so there is nothing for the JS to populate.
    return;
  }

  // Honour the user's reduced-motion preference: arrows render instantly (no
  // draw animation) and the live-indicator pulse is suppressed via CSS.
  const prefersReducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const entities = buildEntityList(trace);
  const entityTeams = computeEntityTeams(trace.events);
  const totalEvents = trace.events.length;
  const svgWidth = MARGIN_LEFT + entities.length * COLUMN_WIDTH + MARGIN_LEFT;
  const svgHeight = totalEvents * ROW_HEIGHT + MARGIN_TOP * 2;

  // --- Live indicator ---
  // The template already renders a live indicator inside the header; only sync
  // its active/inactive state here. (No JS-created duplicate.)
  const liveIndicator = container.querySelector<HTMLElement>('.gestalt-pipeline-live-indicator');
  if (liveIndicator) {
    liveIndicator.classList.toggle('gestalt-pipeline-live-indicator--inactive', !trace.is_live);
  }

  // --- Truncation warning ---
  const truncationWarning = container.querySelector<HTMLElement>('.gestalt-pipeline-truncation-warning');
  if (truncationWarning) {
    truncationWarning.classList.toggle('tw-hidden', !trace.truncated);
  }

  // --- Populate the template-provided scroll container + SVG ---
  // The styled, empty shell is rendered server-side; we query and populate it
  // rather than creating a second copy (the prior append-below defect).
  const scrollContainer = container.querySelector<HTMLElement>('.gestalt-pipeline-container');
  if (!scrollContainer) {
    console.error('gestalt-pipeline: .gestalt-pipeline-container not found');
    return;
  }
  const svg = scrollContainer.querySelector<SVGSVGElement>('.gestalt-pipeline-svg');
  if (!svg) {
    console.error('gestalt-pipeline: .gestalt-pipeline-svg not found');
    return;
  }
  svg.setAttribute('width', String(svgWidth));
  svg.setAttribute('height', String(svgHeight));
  svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
  // The single role="img"+aria-label lives on the container (server-rendered);
  // the inner svg must not duplicate it, so strip any stale role here.
  svg.removeAttribute('role');
  svg.removeAttribute('aria-label');

  // Defs: arrowhead marker. orient=auto rotates the head to the line's terminus
  // direction, so the same marker serves horizontal request lines and the curved
  // self-loop. fill:currentColor makes the head inherit the arrow's colour (the
  // arrow group sets `color` to match its stroke in CSS), so request and response
  // heads match their line colour without a per-variant marker. The marker is NOT
  // attached at path-creation time: SVG markers ignore stroke-dashoffset and would
  // otherwise render at the (un-drawn) end point on the un-started field. It is
  // attached via marker-end only once the line has finished drawing (see
  // animatePath) and removed again on reset (see resetArrows).
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
  marker.setAttribute('id', 'gestalt-pipeline-arrowhead');
  marker.setAttribute('markerWidth', '10');
  marker.setAttribute('markerHeight', '7');
  marker.setAttribute('refX', '9');
  marker.setAttribute('refY', '3.5');
  marker.setAttribute('orient', 'auto');
  const markerPath = createSVGPath('M0,0 L10,3.5 L0,7 Z');
  markerPath.setAttribute('fill', 'currentColor');
  marker.append(markerPath);
  defs.append(marker);
  svg.append(defs);

  // Entity labels (two-line: name + team), bordered box, and lifelines.
  const entityPositions: Record<string, number> = {};
  // Defer rect sizing until after attach because getBBox() returns zeros on a
  // detached SVG; collect the rect+text pairs and size them once attached.
  const entityBoxes: Array<{rect: SVGRectElement; text: SVGTextElement}> = [];
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    const x = MARGIN_LEFT + i * COLUMN_WIDTH + COLUMN_WIDTH / 2;
    entityPositions[entity] = x;

    const box = createSVGRect('gestalt-pipeline-entity-box');
    box.setAttribute('rx', String(ENTITY_BOX_RADIUS));
    box.setAttribute('ry', String(ENTITY_BOX_RADIUS));
    svg.append(box);

    const label = createSVGText(x, MARGIN_TOP, '', 'gestalt-pipeline-entity-label');
    label.append(createSVGTSpan(x, entity, 'gestalt-pipeline-entity-name'));
    const team = entityTeams.get(entity);
    if (team) {
      label.append(createSVGTSpan(x, team, 'gestalt-pipeline-entity-role', TEAM_LINE_DY));
    }
    svg.append(label);
    entityBoxes.push({rect: box, text: label});

    const lifelineStart = MARGIN_TOP + LIFELINE_HEIGHT;
    const lifelineEnd = svgHeight - MARGIN_TOP;
    const lifeline = createSVGLine(x, lifelineStart, x, lifelineEnd, 'gestalt-pipeline-lifeline');
    lifeline.setAttribute('stroke-dasharray', '4,4');
    svg.append(lifeline);
  }

  // sizeEntityBoxes positions/sizes each rounded border around its label using
  // the label's measured bounding box plus padding. Must run after the SVG is in
  // the DOM (getBBox() is zero pre-attach).
  function sizeEntityBoxes(): void {
    for (const {rect, text} of entityBoxes) {
      let bbox: DOMRect;
      try {
        bbox = text.getBBox();
      } catch {
        continue;
      }
      if (bbox.width === 0 && bbox.height === 0) continue;
      rect.setAttribute('x', String(bbox.x - ENTITY_BOX_PAD_X));
      rect.setAttribute('y', String(bbox.y - ENTITY_BOX_PAD_Y));
      rect.setAttribute('width', String(bbox.width + ENTITY_BOX_PAD_X * 2));
      rect.setAttribute('height', String(bbox.height + ENTITY_BOX_PAD_Y * 2));
    }
  }

  // Arrow group
  const arrowGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  arrowGroup.setAttribute('class', 'gestalt-pipeline-arrows');
  svg.append(arrowGroup);

  // Pre-compute arrow geometry
  const arrowData = trace.events.map((event) => {
    const fromX = entityPositions[event.from_entity] ?? 0;
    const toX = entityPositions[event.to_entity] ?? 0;
    const y = MARGIN_TOP + LIFELINE_HEIGHT + event.seq * ROW_HEIGHT + ROW_HEIGHT / 2;

    let d: string;
    const isSelfLoop = event.from_entity === event.to_entity;

    if (isSelfLoop) {
      d = `M ${fromX - 25},${y} Q ${fromX},${y - 50} ${fromX + 25},${y}`;
    } else {
      d = `M ${fromX},${y} L ${toX},${y}`;
    }

    return {event, fromX, toX, y, d, isSelfLoop};
  });

  // --- State ---
  let currentSeq = -1;
  let isPlaying = false;
  let speedMultiplier = 1;
  let visibleStart = 0;
  let visibleEnd = totalEvents;
  const arrowElements = new Map<number, SVGGElement>();

  // --- Control queries (null-safe; empty-state renders no controls) ---
  const playBtn = container.querySelector<HTMLButtonElement>('.gestalt-pipeline-play-btn');
  const playLabel = playBtn?.querySelector<HTMLElement>('.js-pipeline-play-label') ?? null;
  const stepBtn = container.querySelector<HTMLButtonElement>('.gestalt-pipeline-step-btn');
  const resetBtn = container.querySelector<HTMLButtonElement>('.gestalt-pipeline-reset-btn');
  const replayBtn = container.querySelector<HTMLButtonElement>('.gestalt-pipeline-replay-btn');
  const speedSlider = container.querySelector<HTMLInputElement>('.gestalt-pipeline-speed-slider');
  const speedValue = container.querySelector<HTMLElement>('.gestalt-pipeline-speed-value');
  const statusRegion = container.querySelector<HTMLElement>('.gestalt-pipeline-status');

  const playLabelText = playBtn?.getAttribute('data-play-label') ?? 'Play';
  const pauseLabelText = playBtn?.getAttribute('data-pause-label') ?? 'Pause';

  // setPlayLabel toggles ONLY the inner label span so the button's icon and any
  // i18n node it wraps survive (never overwrite the whole button's textContent).
  function setPlayLabel(playing: boolean): void {
    if (playLabel) playLabel.textContent = playing ? pauseLabelText : playLabelText;
  }

  function updateLiveRegion(text: string): void {
    if (statusRegion) statusRegion.textContent = text;
  }

  function updateEventInfo(): void {
    if (currentSeq < 0) {
      updateLiveRegion('');
    } else {
      const ev = trace.events[currentSeq];
      updateLiveRegion(`Event ${currentSeq + 1} of ${totalEvents}: ${ev.from_entity} ${ev.label} issue to ${ev.to_entity}`);
    }
  }

  // --- Arrow creation ---
  function createArrowElement(data: typeof arrowData[0]): SVGGElement {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('class', `gestalt-pipeline-arrow gestalt-pipeline-arrow--${data.event.direction}`);

    const path = createSVGPath(data.d, 'gestalt-pipeline-arrow-path');
    // The arrowhead marker is intentionally NOT attached here. SVG markers ignore
    // stroke-dashoffset, so a marker-end set at creation renders the head at the
    // (un-drawn) end point on the un-started field. It is attached only once the
    // line has finished drawing (animatePath) and removed again on reset
    // (resetArrows), so the head never floats on an un-started or reset field.
    path.setAttribute('data-seq', String(data.event.seq));
    path.setAttribute('tabindex', '0');

    if (data.event.direction === 'response') {
      path.setAttribute('stroke-dasharray', '8,4');
    }

    const titleText = `${data.event.timestamp} - ${data.event.label}\n${data.event.from_entity} -> ${data.event.to_entity}`;
    path.append(createSVGTitle(titleText));
    // Mirror the <title> onto an aria-label so the focusable arrow path is
    // announced by assistive tech when it receives keyboard focus.
    path.setAttribute('aria-label', titleText.replace(/\n/g, ', '));

    group.append(path);
    arrowGroup.append(group);

    let length: number;
    try {
      length = path.getTotalLength();
    } catch {
      length = data.isSelfLoop ? 100 : Math.abs(data.toX - data.fromX);
    }

    path.setAttribute('data-path-length', String(length));
    path.style.setProperty('stroke-dasharray', String(length));
    path.style.setProperty('stroke-dashoffset', String(length));
    path.style.setProperty('transition-property', 'stroke-dashoffset');
    path.style.setProperty('transition-duration', '0ms');
    path.style.setProperty('transition-timing-function', 'ease-in-out');

    return group;
  }

  // --- Virtualized rendering ---
  function renderVisibleArrows(): void {
    if (totalEvents <= VIRTUALIZATION_THRESHOLD) {
      if (arrowElements.size === 0) {
        for (const data of arrowData) {
          const group = createArrowElement(data);
          arrowElements.set(data.event.seq, group);
        }
      }
      return;
    }

    for (const [seq, group] of arrowElements) {
      if (seq < visibleStart || seq >= visibleEnd) {
        group.remove();
        arrowElements.delete(seq);
      }
    }

    for (let seq = visibleStart; seq < visibleEnd; seq++) {
      if (!arrowElements.has(seq)) {
        const data = arrowData[seq];
        const group = createArrowElement(data);
        arrowElements.set(seq, group);
      }
    }
  }

  function updateVisibleRange(): void {
    const scrollTop = scrollContainer!.scrollTop;
    const containerHeight = scrollContainer!.clientHeight;
    const viewportRows = Math.ceil(containerHeight / ROW_HEIGHT);
    const slice = getVisibleSlice(totalEvents, scrollTop, viewportRows);
    visibleStart = slice.start;
    visibleEnd = slice.end;
    renderVisibleArrows();
  }

  // Initial render
  if (totalEvents > VIRTUALIZATION_THRESHOLD) {
    const viewportRows = Math.ceil(MAX_CONTAINER_HEIGHT / ROW_HEIGHT);
    visibleStart = 0;
    visibleEnd = Math.min(totalEvents, viewportRows + BUFFER_ROWS);
    scrollContainer.addEventListener('scroll', updateVisibleRange);
  }
  renderVisibleArrows();

  // Size the entity boxes now that the SVG is attached to the live DOM.
  sizeEntityBoxes();

  // --- Animation ---
  function animatePath(group: SVGGElement): Promise<void> {
    const path = group.querySelector<SVGPathElement>('.gestalt-pipeline-arrow-path')!;
    return new Promise((resolve) => {
      // Reduced-motion: snap arrows in with zero transition.
      const duration = prefersReducedMotion ? 0 : BASE_ANIMATION_DURATION * speedMultiplier;
      path.style.setProperty('transition-duration', `${duration}ms`);

      // showArrowhead reveals the arrowhead at the line terminus only once the
      // draw has completed, so it is never visible on the un-started field. Under
      // reduced-motion the duration is 0 and the line renders instantly, so the
      // head is attached immediately (via the fallback resolve) rather than
      // floating ahead of the line.
      const showArrowhead = () => {
        path.setAttribute('marker-end', 'url(#gestalt-pipeline-arrowhead)');
      };

      let fallbackTimeout: ReturnType<typeof setTimeout>;
      const onTransitionEnd = () => {
        path.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallbackTimeout);
        showArrowhead();
        resolve();
      };
      path.addEventListener('transitionend', onTransitionEnd);
      path.style.setProperty('stroke-dashoffset', '0');
      path.classList.add('is-visible');

      fallbackTimeout = setTimeout(() => {
        path.removeEventListener('transitionend', onTransitionEnd);
        showArrowhead();
        resolve();
      }, duration + 100);
    });
  }

  function animateEvent(seq: number): Promise<void> {
    const path = arrowElements.get(seq);
    if (!path) {
      updateVisibleRange();
      const newPath = arrowElements.get(seq);
      if (!newPath) return Promise.resolve();
      return animatePath(newPath);
    }
    return animatePath(path);
  }

  function resetArrows(): void {
    for (const group of arrowElements.values()) {
      const path = group.querySelector<SVGPathElement>('.gestalt-pipeline-arrow-path')!;
      path.classList.remove('is-visible');
      // Strip the arrowhead so it does not float on the reset (un-started) field;
      // it is re-attached by animatePath when the line is next drawn.
      path.removeAttribute('marker-end');
      const length = path.getAttribute('data-path-length') || '0';
      path.style.setProperty('transition-duration', '0ms');
      path.style.setProperty('stroke-dashoffset', length);
    }
    currentSeq = -1;
    isPlaying = false;
    setPlayLabel(false);
    if (stepBtn) stepBtn.disabled = false;
    updateEventInfo();
  }

  // --- Playback logic ---
  async function playAll(): Promise<void> {
    if (isPlaying) return;
    isPlaying = true;
    setPlayLabel(true);
    if (stepBtn) stepBtn.disabled = true;

    for (let seq = currentSeq + 1; seq < totalEvents; seq++) {
      if (!isPlaying) break;
      currentSeq = seq;
      updateEventInfo();

      // Auto-scroll during play for virtualized traces
      if (totalEvents > VIRTUALIZATION_THRESHOLD) {
        const scrollTop = scrollContainer!.scrollTop;
        const containerHeight = scrollContainer!.clientHeight;
        const viewportRows = Math.ceil(containerHeight / ROW_HEIGHT);
        const firstVisibleRow = Math.floor(scrollTop / ROW_HEIGHT);
        const lastVisibleRow = firstVisibleRow + viewportRows;

        if (seq > lastVisibleRow - 1) {
          const newScrollTop = (seq - viewportRows + 1) * ROW_HEIGHT;
          requestAnimationFrame(() => {
            scrollContainer!.scrollTop = Math.max(0, newScrollTop);
            updateVisibleRange();
          });
        }
      }

      await animateEvent(seq);
    }

    isPlaying = false;
    setPlayLabel(false);
    if (stepBtn) stepBtn.disabled = false;
  }

  function pause(): void {
    isPlaying = false;
    setPlayLabel(false);
    if (stepBtn) stepBtn.disabled = false;
  }

  async function step(): Promise<void> {
    if (isPlaying) {
      pause();
      return;
    }
    if (currentSeq + 1 >= totalEvents) return;
    currentSeq++;
    updateEventInfo();
    await animateEvent(currentSeq);
  }

  async function togglePlay(): Promise<void> {
    if (isPlaying) {
      pause();
    } else {
      await playAll();
    }
  }

  // --- Control event listeners (each guarded; controls may be absent) ---
  playBtn?.addEventListener('click', async () => {
    await togglePlay();
  });

  stepBtn?.addEventListener('click', async () => {
    await step();
  });

  resetBtn?.addEventListener('click', () => {
    resetArrows();
  });

  // Header replay button: rewind then play from the start.
  replayBtn?.addEventListener('click', async () => {
    resetArrows();
    await playAll();
  });

  speedSlider?.addEventListener('input', () => {
    speedMultiplier = clamp(parseFloat(speedSlider.value), 0.25, 4);
    if (speedValue) speedValue.textContent = `${speedMultiplier}x`;
  });

  // --- Keyboard ---
  container.addEventListener('keydown', async (e) => {
    const target = e.target as HTMLElement;
    if (!target) return;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        await togglePlay();
        break;
      case 'ArrowRight':
        e.preventDefault();
        await step();
        break;
      case 'r':
      case 'R':
        e.preventDefault();
        resetArrows();
        break;
    }
  });
}

registerGlobalInitFunc('initGestaltPipeline', initGestaltPipeline);
